/**
 * Configuración de métodos de pago
 * Centraliza datos para fácil actualización y escalabilidad
 */

// Configuración de Bold
export interface BoldConfig {
  apiKey: string;
  checkoutUrl: string;
  scriptUrl: string;
  timeout: number; // milliseconds
  redirectUrl: string;
}

export interface BankAccount {
  account: string;
  accountType: 'Ahorros' | 'Corriente';
  lastDigits: string;
  name: string;
  nit: string;
}

export interface BreBKey {
  key: string;
  name: string;
}

export interface NequiAccount {
  number: string;
  name: string;
}

export interface QRPayment {
  imageUrl: string;
  whatsappLink?: string;
}

export interface BankPaymentConfig {
  qr: QRPayment;
  bancolombia: BankAccount;
  breB: BreBKey;
  nequi: NequiAccount;
  supportedBanks: string[];
  whatsappSupport: string;
}

/**
 * Datos bancarios de TE ALQUILAMOS S.A.S
 */
export const BANK_PAYMENT_CONFIG: BankPaymentConfig = {
  qr: {
    imageUrl: '/qr-bancolombia.jpg',
    whatsappLink: 'https://wa.me/p/25240524268871838/573023371476'
  },

  bancolombia: {
    account: '786-488007-96',
    accountType: 'Ahorros',
    lastDigits: '*0796',
    name: 'TE ALQUILAMOS S.A.S',
    nit: '900.890.457'
  },

  breB: {
    key: '0040399396',
    name: 'TE ALQUILAMOS S.A.S'
  },

  nequi: {
    number: '3003913251',
    name: 'Al Herr'
  },

  supportedBanks: [
    'Bancolombia',
    'Nequi',
    'Daviplata',
    'Davivienda',
    'BBVA',
    'Colpatria',
    'Banco de Bogotá',
    'Banco Popular',
    'Banco Caja Social'
  ],

  whatsappSupport: '+57 302 337 1476'
};

/**
 * Configuración de Bold para pagos con tarjeta
 */
export const BOLD_CONFIG: BoldConfig = {
  apiKey: process.env.NEXT_PUBLIC_BOLD_API_KEY || '',
  checkoutUrl: 'https://checkout.bold.co',
  scriptUrl: 'https://checkout.bold.co/library/boldPaymentButton.js',
  timeout: 10000, // 10 segundos
  redirectUrl: typeof window !== 'undefined' ? `${window.location.origin}/payment/confirm` : '/payment/confirm'
};

/**
 * Configuración de depósito mínimo para reservas
 */
export interface BookingDepositConfig {
  minimumDepositNights: number; // Número de noches requeridas como depósito mínimo
  allowPartialPayment: boolean; // Permitir pagos parciales
  allowExtraDeposit: boolean; // Permitir depositar más del mínimo
  quickButtonsPercentages: number[]; // Porcentajes para botones rápidos
}

export const BOOKING_DEPOSIT_CONFIG: BookingDepositConfig = {
  minimumDepositNights: 1, // Mínimo 1 noche
  allowPartialPayment: true,
  allowExtraDeposit: true,
  quickButtonsPercentages: [25, 50, 100] // Botones rápidos: fácil de editar (ej: [30, 50, 75, 100])
};

/**
 * Formatea un número como moneda colombiana
 */
export function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('es-CO')}`;
}

/**
 * Copia texto al portapapeles
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Fallback para navegadores antiguos
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      console.error('Error al copiar:', err);
      return false;
    }
  }
}
